import type { DocSection } from '../types'
import { FIG_CAMPAIGN_MODE } from '../figures'

export const dialerSection: DocSection = {
  id: 'dialer',
  title: 'Modes de numérotation (appels automatiques)',
  icon: 'server',
  accent: '#0D9488',
  summary: "Aperçu, progressif, power, prédictif : qui compose, ce que voit l'agent, et les réglages du numérateur.",
  minRole: 'team_admin',
  articles: [
    {
      id: 'principe',
      title: 'Les 4 modes de numérotation',
      summary: "Qui compose le numéro, et ce que ça change pour l'agent.",
      icon: 'server',
      blocks: [
        { type: 'p', text: "Le **Mode de numérotation** d'une campagne décide **qui compose** les numéros et **comment l'agent travaille**. Il se choisit campagne par campagne." },
        { type: 'table', headers: ['Mode', 'Qui compose', 'Ce que fait l\'agent'], rows: [
          ['**Aperçu**', "L'agent", "Il voit la fiche puis clique **Appeler**. Numéros composés **l'un après l'autre** côté agent. Mode par défaut."],
          ['**Progressif**', "Le système (1 ligne / agent)", "Il **s'inscrit** (« Recevoir les appels auto ») et **attend** : seules les **vraies personnes** lui sont connectées."],
          ['**Power**', "Le système (**N lignes / agent**)", "Idem progressif, mais le système compose **plusieurs lignes par agent** (champ **Appels par agent**) pour réduire l'attente."],
          ['**Prédictif**', "Le système (anticipe)", "Idem power : le système **sur-numérote** en anticipant les décrochages, et connecte chaque personne au **premier agent libre**."],
        ] },
        { type: 'callout', variant: 'warning', title: 'Power & Prédictif = trunk multicanal requis', text: "Composer **plusieurs lignes à la fois** exige un trunk SIP **multicanal**. Sans lui (trunk mono-canal), ces modes sont **indisponibles** et le système reste à **1 appel à la fois**. Voir [[dialer/multicanal|Trunk multicanal]]." },
        { type: 'seealso', items: [
          { to: 'dialer/cote-agent', label: "Ce que voit l'agent selon le mode" },
          { to: 'dialer/champs', label: 'Tous les réglages du numérateur' },
          { to: 'dialer/multicanal', label: 'Trunk multicanal (Power/Prédictif)' },
          { to: 'dialer/repondeurs', label: 'Et les répondeurs ?' },
        ] },
      ],
    },
    {
      id: 'cote-agent',
      title: "Ce que voit l'agent selon le mode",
      summary: "Aperçu = il compose ; serveur = il s'inscrit et reçoit.",
      icon: 'phone',
      blocks: [
        { type: 'h', text: 'Mode Aperçu' },
        { type: 'p', text: "Dans **Ma file d'appels**, l'agent voit **« Démarrer les appels »**. Chaque numéro est composé automatiquement l'un après l'autre ; après chaque appel il **remplit la fiche** puis le suivant se lance. C'est lui qui mène." },
        { type: 'h', text: 'Modes serveur (Progressif / Power / Prédictif)' },
        { type: 'p', text: "L'agent ne compose plus. À la place, il voit un interrupteur **« Recevoir les appels auto »** :" },
        { type: 'steps', items: [
          "Il se met **Disponible**, puis active **Recevoir les appels auto**.",
          "S'affiche alors **« Le système compose… »** avec un **minuteur d'attente** (temps depuis le dernier appel).",
          "Le **système compose en arrière-plan** et filtre les répondeurs.",
          "Dès qu'une **personne décroche**, l'appel **arrive comme un appel entrant** : la fiche s'ouvre, l'agent parle, puis qualifie comme d'habitude.",
          "Le minuteur **repart à zéro** et l'attente reprend.",
        ] },
        { type: 'callout', variant: 'info', title: 'Le mode Prédictif en détail', text: "Le système **passe l'appel en arrière-plan** (un minuteur tourne chez l'agent, selon **Appels par agent**). Dès qu'une personne **répond**, l'appel est **connecté instantanément** à un agent **disponible**. S'il n'y a **aucun agent libre**, la ligne est **raccrochée** (appel abandonné). Les agents sont servis en **FIFO** (le plus anciennement en attente d'abord)." },
      ],
    },
    {
      id: 'champs',
      title: 'Tous les réglages du numérateur',
      summary: "Appels par agent, distribution, priorité, tentatives, délais, temps de réponse.",
      icon: 'settings',
      blocks: [
        { type: 'kv', items: [
          { k: 'Mode de numérotation', v: "Aperçu / Progressif / Power / Prédictif (voir [[dialer/principe|Les 4 modes]])." },
          { k: 'Appels par agent', v: "**Power/Prédictif uniquement** : nombre de lignes composées par agent libre (le sur-numéro). En aperçu/progressif, forcé à 1." },
          { k: 'Distribution', v: "Ordre de pioche des numéros : **Ordre d'arrivée (FIFO)** par défaut." },
          { k: 'Priorité', v: "Entre campagnes : une priorité plus haute est servie en premier par le moteur." },
          { k: 'Tentatives par contact', v: "Nombre d'appels max sur un même numéro avant de l'épuiser (défaut 3)." },
          { k: 'Heures entre les tentatives', v: "Délai minimum avant de **rappeler** un numéro non abouti (anti-harcèlement)." },
          { k: "Temps d'attente de la réponse", v: "Durée de **sonnerie** avant d'abandonner un appel sans réponse — **5 à 300 s** (défaut 45)." },
        ] },
        { type: 'callout', variant: 'tip', text: "Un numéro récemment appelé reste **en attente** tant que le délai de relance n'est pas écoulé — il n'est pas redistribué avant." },
      ],
    },
    {
      id: 'multicanal',
      title: 'Trunk multicanal (Power / Prédictif)',
      summary: "Le sur-numéro exige un opérateur qui accepte plusieurs appels simultanés.",
      icon: 'server',
      minRole: 'admin',
      blocks: [
        { type: 'p', text: "Les modes **Power** et **Prédictif** composent **plusieurs lignes en même temps**. C'est impossible avec un trunk **mono-canal** (un seul appel sortant à la fois — au-delà, l'opérateur renvoie une erreur). Il faut donc **déclarer** que votre SIP est multicanal." },
        { type: 'steps', items: [
          "**Téléphonie → Trunks → modifiez** votre trunk sortant.",
          "Cochez **« Mon SIP est multicanal »** et indiquez le **nombre de canaux simultanés** que votre opérateur autorise.",
          "Sur la **campagne**, sélectionnez le trunk en **Opérateur sortant**, puis choisissez **Power** ou **Prédictif**.",
        ] },
        { type: 'callout', variant: 'warning', title: 'Sécurité intégrée', text: "Tant qu'aucun trunk multicanal n'est déclaré sur la campagne, **Power et Prédictif sont refusés** et le système reste à **1 appel à la fois** — impossible de saturer un trunk mono-canal par erreur." },
        { type: 'callout', variant: 'info', text: "La case **ne crée pas** la capacité : elle l'**autorise**. Votre **opérateur** doit réellement accepter plusieurs canaux simultanés, sinon les appels en trop échoueront." },
      ],
    },
    {
      id: 'activer',
      title: 'Activer le mode progressif',
      summary: 'Le réglage se trouve sur la campagne.',
      icon: 'check-circle',
      blocks: [
        { type: 'p', text: "Le mode se choisit **campagne par campagne** : vous pouvez avoir des campagnes en aperçu et d'autres en progressif." },
        { type: 'figure', svg: FIG_CAMPAIGN_MODE, alt: "Sélecteur du mode d'appel d'une campagne", caption: "Dans l'édition de la campagne, choisissez « Progressif »." },
        { type: 'steps', items: [
          "Ouvrez la campagne et modifiez-la.",
          "Dans **Mode d'appel**, choisissez **Progressif (1 appel par agent libre)**.",
          "Vérifiez que la campagne est **Active**, dans sa **plage horaire**, et contient des **numéros** à appeler.",
          "Enregistrez. Les agents disponibles commenceront à recevoir les appels une fois mis en relation.",
        ] },
        { type: 'callout', variant: 'info', title: 'Un appel à la fois par défaut', text: "Avec un trunk **mono-canal**, le système reste à **un appel à la fois** (sûr pour votre installation). Pour composer **plusieurs lignes en parallèle** (Power/Prédictif), déclarez un trunk **multicanal** — voir [[dialer/multicanal|Trunk multicanal]]." },
      ],
    },
    {
      id: 'repondeurs',
      title: 'Et les répondeurs ?',
      summary: 'Comment le système les écarte, et quoi faire s\'il se trompe.',
      icon: 'phone-off',
      blocks: [
        { type: 'p', text: "En mode progressif, le système **écoute le début de chaque appel décroché** pour deviner s'il s'agit d'une personne ou d'un répondeur :" },
        { type: 'list', items: [
          "**Une personne** → l'appel est mis en relation avec un agent disponible (sa fiche s'ouvre).",
          "**Un répondeur** → l'appel est raccroché, le résultat « Messagerie » est noté **automatiquement**, et le numéro suivant est composé.",
          "**En cas de doute, le système met quand même en relation** — pour ne jamais raccrocher au nez d'une vraie personne.",
        ] },
        { type: 'callout', variant: 'warning', title: "À retenir sur le mode aperçu", text: "En mode **aperçu**, l'option « passer automatiquement les appels non aboutis » saute les numéros occupés ou sans réponse, mais **ne reconnaît pas** un répondeur qui décroche. Seul le mode **progressif** sait écarter les répondeurs." },
        { type: 'callout', variant: 'info', title: "Si le tri se trompe", text: "La reconnaissance des répondeurs est **réglée côté technique**, pas dans l'application. Si vous constatez que des répondeurs arrivent aux agents (ou l'inverse), **signalez-le à votre administrateur** : il ajustera la sensibilité." },
      ],
    },
  ],
}
